import { LocationApi, LocationSearchItem } from '@deckee/api-app-main';
import {
  Box,
  Card,
  Input,
  MessageBlock,
  NavList,
  SearchIcon,
} from '@deckee/deck-hand';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'throttle-debounce';
import styled, { css, useTheme, type DefaultTheme } from 'styled-components';
import apiConfiguration from '../../../../../services/backend/apiConfiguration';
import { callEndpoint, telemetry } from '../../../../../services/backend';
import LogoSpinner from '../../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import LocationSearchCard from '../SearchLocationCard/LocationSearchCard';

const api = new LocationApi(apiConfiguration);

const storyMarkerRelatedSearchTerms =
  window.DECKEE_CONFIG?.storyMarkerRelatedSearchTerms;
const storyMarkersHiddenFromSearch: string | string[] =
  window.DECKEE_CONFIG?.storyMarkersHiddenFromSearch ?? [];

interface StoryMarker extends LocationSearchItem {
  owningInfluencer: {
    id: string;
  };
  title: string;
}

export interface SplitLocationSearchResults {
  googlePlaces: LocationSearchItem[];
  deckeeMarkers: LocationSearchItem[];
  storyMarkers: LocationSearchItem[];
  googlePlacesError?: boolean;
  deckeeMarkersError?: boolean;
}

const DEFAULT_SEARCH_TERM = '';

const fetchMatchingStoryMarkers = async (
  searchTermValue: string,
): Promise<StoryMarker[]> => {
  try {
    const { markers: storyMarkers } = await callEndpoint(
      callEndpoint.endpoints.getStoryMarkers,
    );

    return storyMarkers
      .filter(
        (story: StoryMarker) =>
          !storyMarkersHiddenFromSearch.includes(story.owningInfluencer.id),
      )
      .filter((story: StoryMarker) => {
        const matcher = searchTermValue.toLowerCase();
        const titleMatch = story.title.toLowerCase().includes(matcher);

        if (
          storyMarkerRelatedSearchTerms?.[story.owningInfluencer.id]?.synonyms
        ) {
          const { synonyms } =
            storyMarkerRelatedSearchTerms[story.owningInfluencer.id];
          const synonymMatch =
            synonyms?.toLowerCase().includes(matcher) ?? false;
          return titleMatch || synonymMatch;
        }

        return titleMatch;
      });
  } catch (error) {
    telemetry(
      telemetry.ERROR,
      `[LocationSearch.fetchMatchingStoryMarkers] Error fetching story markers: ${JSON.stringify(
        error,
      )}`,
    );
    return [];
  }
};

const StyledLogoSpinner = styled(LogoSpinner)`
  width: 100%;
  height: 100% !important;
  min-height: 100px;
`;

const pinnedSearchInputBarStyles = (theme: DefaultTheme) => css`
  flex-shrink: 0;
  z-index: 1;
  box-shadow:
    0 1px ${theme.space[2]}px
      color-mix(in srgb, ${theme.colors.black} 7%, transparent),
    0 0 ${theme.space[1]}px
      color-mix(in srgb, ${theme.colors.black} 7%, transparent);
`;

const StickySearchLayout = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const StickySearchResultsArea = styled(Box)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
`;

const SearchInputBarWrapper = styled(Box)<{ $pinned?: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  margin-left: -${({ theme }) => theme.space[2]}px;
  margin-right: -${({ theme }) => theme.space[2]}px;
  padding: ${({ theme }) => theme.space[3]}px ${({ theme }) => theme.space[2]}px;
  margin-bottom: ${({ theme }) => theme.space[2]}px;
  ${({ $pinned, theme }) => $pinned && pinnedSearchInputBarStyles(theme)}
`;

const StickySearchInputWrapper = styled(Box)`
  ${({ theme }) => pinnedSearchInputBarStyles(theme)}
  background: ${({ theme }) => theme.colors.white};
  margin-left: -${({ theme }) => theme.space[2]}px;
  margin-right: -${({ theme }) => theme.space[2]}px;
  padding: ${({ theme }) => theme.space[3]}px ${({ theme }) => theme.space[2]}px
    ${({ theme }) => theme.space[2]}px;
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

const SearchInputTrailingBarWrapper = styled(SearchInputBarWrapper)`
  padding-right: ${({ theme }) => theme.space[2.5]}px;
`;

export interface LocationSearchProps {
  /**
   * Callback when a location item is selected
   */
  onSelectLocation: (searchItem: LocationSearchItem) => void | Promise<void>;

  /**
   * The latitude to center the search around
   */
  searchCenterLat?: number;

  /**
   * The longitude to center the search around
   */
  searchCenterLon?: number;

  /**
   * Placeholder text for the search input
   */
  placeholder?: string;

  /**
   * Optional additional actions to display above search results (e.g., "Choose on map")
   */
  additionalActions?: ReactNode;

  /**
   * Auto focus the input on mount
   */
  autoFocus?: boolean;

  /**
   * Initial search term
   */
  initialSearchTerm?: string;

  /**
   * Callback when search term changes
   */
  onSearchTermChange?: (searchTerm: string) => void;

  /**
   * Custom render function for search results
   */
  renderResults?: (
    items: LocationSearchItem[],
    onSelect: (item: LocationSearchItem) => void,
  ) => ReactNode;

  /**
   * When true, runs separate location searches for Google Places and Deckee
   * markers (via the `source` param) instead of a single combined search.
   */
  splitSourceSearch?: boolean;

  /**
   * Render function for split-source search results. Used with
   * `splitSourceSearch` to display results from each source in separate cards.
   */
  renderSplitResults?: (
    results: SplitLocationSearchResults,
    onSelect: (item: LocationSearchItem) => void,
  ) => ReactNode;

  /**
   * Optional override for the search behaviour. When provided and it resolves to
   * an array, those results are used instead of the default location search.
   * Return `null` to fall back to the default search (places + story markers).
   */
  overrideSearch?: (
    searchTerm: string,
    context: { lat: number; lon: number; signal?: AbortSignal },
  ) => Promise<LocationSearchItem[] | null>;

  /**
   * When false, custom `renderResults` output is not wrapped in an outer Card.
   * Use when the renderer returns its own card(s), e.g. weather favourites.
   */
  wrapResultsInCard?: boolean;

  /**
   * When true, renders the search input on a white strip that extends to the
   * layout edges so it visually connects with the page header.
   */
  connectSearchInputToHeader?: boolean;

  /**
   * Optional node rendered to the right of the search input (e.g. a Cancel
   * button) when the page has no header bar.
   */
  searchInputTrailing?: ReactNode;

  /**
   * When true, keeps the search input pinned to the top while results scroll
   * beneath it. Uses a nested scroll area so iOS overscroll bounce does not
   * move the search input.
   */
  stickySearchInput?: boolean;
}

/**
 * Reusable location search component that handles searching and displaying results.
 * The action when selecting a location is customizable via the onSelectLocation prop.
 */
const LocationSearch: FC<LocationSearchProps> = ({
  onSelectLocation,
  searchCenterLat,
  searchCenterLon,
  placeholder = 'Search for a location',
  additionalActions,
  autoFocus = true,
  initialSearchTerm = '',
  onSearchTermChange,
  renderResults,
  splitSourceSearch = false,
  renderSplitResults,
  overrideSearch,
  wrapResultsInCard = true,
  connectSearchInputToHeader = false,
  searchInputTrailing,
  stickySearchInput = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const {
    colors: { primary },
  } = useTheme();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [searchLocationItems, setSearchLocationItems] = useState<
    LocationSearchItem[]
  >([]);
  const [splitSearchResults, setSplitSearchResults] =
    useState<SplitLocationSearchResults | null>(null);

  // Use refs to store the latest search center coordinates
  // This prevents the debounced function from being recreated when coordinates change
  const searchCenterLatRef = useRef(searchCenterLat);
  const searchCenterLonRef = useRef(searchCenterLon);
  const overrideSearchRef = useRef(overrideSearch);
  const splitSourceSearchRef = useRef(splitSourceSearch);

  // Update refs when coordinates change
  useEffect(() => {
    searchCenterLatRef.current = searchCenterLat;
    searchCenterLonRef.current = searchCenterLon;
  }, [searchCenterLat, searchCenterLon]);

  // Keep the latest override available to the stable debounced searcher
  useEffect(() => {
    overrideSearchRef.current = overrideSearch;
  }, [overrideSearch]);

  useEffect(() => {
    splitSourceSearchRef.current = splitSourceSearch;
  }, [splitSourceSearch]);

  const updateLocationSearchTerm = useMemo(
    () =>
      debounce(200, async (event, controller?: AbortController) => {
        try {
          if (controller?.signal?.aborted) {
            return;
          }

          // Use refs to get the latest coordinates
          const lat = searchCenterLatRef.current;
          const lon = searchCenterLonRef.current;

          if (!lat || !lon) {
            return;
          }
          setIsLoading(true);
          // Clear any prior error at the start of a new search (replaces the
          // previous error-driven effect re-run, which caused a retry loop).
          setError(false);

          const termText = event.target.value;
          const searchTermValue = termText.trim();

          // Empty search = no results
          if (!searchTermValue) {
            setSearchLocationItems([]);
            setSplitSearchResults(null);
            setIsLoading(false);
            return;
          }

          // Allow callers to override the search (e.g. weather marine-station
          // search). Returning null falls back to the default search below.
          const overrideSearchFn = overrideSearchRef.current;
          if (overrideSearchFn) {
            const overriddenResults = await overrideSearchFn(searchTermValue, {
              lat,
              lon,
              signal: controller?.signal,
            });

            if (controller?.signal?.aborted) {
              return;
            }

            if (overriddenResults) {
              setSearchLocationItems(overriddenResults);
              setSplitSearchResults(null);
              setIsLoading(false);
              return;
            }
          }

          const latLon = { lat, lon };

          if (splitSourceSearchRef.current) {
            const [googleResult, deckeeResult] = await Promise.allSettled([
              api.locationsSearch(
                {
                  locationsSearchRequest: {
                    searchTerm: searchTermValue,
                    latLon,
                    source: 'googlePlaces',
                  },
                },
                { signal: controller?.signal },
              ),
              api.locationsSearch(
                {
                  locationsSearchRequest: {
                    searchTerm: searchTermValue,
                    latLon,
                    source: 'deckeeMarkers',
                  },
                },
                { signal: controller?.signal },
              ),
            ]);

            if (controller?.signal?.aborted) {
              return;
            }

            let googlePlaces: LocationSearchItem[] = [];
            let deckeeMarkers: LocationSearchItem[] = [];
            let googlePlacesError = false;
            let deckeeMarkersError = false;

            if (googleResult.status === 'fulfilled') {
              googlePlaces = googleResult.value.data ?? [];
            } else {
              googlePlacesError = true;
              telemetry(
                telemetry.ERROR,
                `[LocationSearch.updateLocationSearchTerm] Error retrieving Google Places ${JSON.stringify(
                  googleResult.reason,
                )}`,
              );
            }

            if (deckeeResult.status === 'fulfilled') {
              deckeeMarkers = deckeeResult.value.data ?? [];
            } else {
              deckeeMarkersError = true;
              telemetry(
                telemetry.ERROR,
                `[LocationSearch.updateLocationSearchTerm] Error retrieving Deckee markers ${JSON.stringify(
                  deckeeResult.reason,
                )}`,
              );
            }

            const matchingStoryMarkers =
              await fetchMatchingStoryMarkers(searchTermValue);

            setSplitSearchResults({
              googlePlaces,
              deckeeMarkers,
              storyMarkers: matchingStoryMarkers,
              googlePlacesError,
              deckeeMarkersError,
            });
            setSearchLocationItems([]);
            setIsLoading(false);
            return;
          }

          // Search for regular locations (includes BEN markers)
          const { data: locationResults } = await api.locationsSearch(
            {
              locationsSearchRequest: {
                searchTerm: searchTermValue,
                latLon,
              },
            },
            {
              signal: controller?.signal,
            },
          );

          let results: LocationSearchItem[] = locationResults;

          const matchingStoryMarkers =
            await fetchMatchingStoryMarkers(searchTermValue);

          if (matchingStoryMarkers.length > 0) {
            results = [...matchingStoryMarkers, ...results];
          }

          setSearchLocationItems(results);
          setSplitSearchResults(null);
        } catch (error) {
          if (controller?.signal?.aborted) {
            // It's not an issue, another request was initiated
            return;
          }
          setError(true);
          telemetry(
            telemetry.ERROR,
            `[LocationSearch.updateLocationSearchTerm] Error retrieving locations ${JSON.stringify(
              error,
            )}`,
          );
        } finally {
          setIsLoading(false);
        }
      }),
    [], // Empty dependency array - function is stable, uses refs for latest values
  );

  useEffect(() => {
    const controller = new AbortController();
    updateLocationSearchTerm(
      { target: { value: searchTerm || DEFAULT_SEARCH_TERM } },
      controller,
    );

    return () => {
      controller.abort();
    };
    // Re-run the search when the term changes or when the override changes
    // identity (e.g. the marine filter chip switches). `hasError` is
    // deliberately NOT a dependency: a failed search sets the error flag, and
    // depending on it here would re-run the search, fail again, and loop
    // (flashing the loading mask). The error is cleared when a new search
    // starts (see updateLocationSearchTerm).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, overrideSearch, splitSourceSearch]);

  const handleLocationSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setSearchTerm(newValue);
      onSearchTermChange?.(newValue);
    },
    [onSearchTermChange],
  );

  const handleSelectSearchLocationCard = async (
    searchItem: LocationSearchItem,
  ) => {
    setIsLoading(true);
    try {
      await onSelectLocation(searchItem);
    } catch (error) {
      setError(true);
      telemetry(
        telemetry.ERROR,
        `[LocationSearch.handleSelectSearchLocationCard] Error in onSelectLocation callback ${JSON.stringify(
          error,
        )}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const searchInput = (
    <Input
      autoFocus={autoFocus}
      clearable
      type="text"
      aria-label="search"
      placeholder={placeholder}
      startEnhancer={() => <SearchIcon color={primary} />}
      value={searchTerm}
      onChange={handleLocationSearchChange}
    />
  );

  const resultsContent = (
    <>
      {isLoading && <StyledLogoSpinner background />}
      {!isLoading && renderResults ? (
        renderResults(searchLocationItems, handleSelectSearchLocationCard)
      ) : (
        <NavList grouped>
          {!isLoading &&
            searchLocationItems &&
            searchLocationItems.length > 0 &&
            searchLocationItems.map((searchLocationItem) => (
              <LocationSearchCard
                key={`${searchLocationItem.placeId}-${searchLocationItem.markerId}`}
                title={searchLocationItem.title}
                markerType={searchLocationItem.markerType}
                placeDescription={searchLocationItem.placeDescription}
                onSelectCard={() =>
                  handleSelectSearchLocationCard(searchLocationItem)
                }
              />
            ))}
        </NavList>
      )}
    </>
  );

  const resultsPanel = wrapResultsInCard ? (
    <Card position="relative" minHeight={isLoading ? '200px' : 'none'}>
      {resultsContent}
    </Card>
  ) : (
    <Box position="relative" minHeight={isLoading ? '200px' : 'none'}>
      {resultsContent}
    </Box>
  );

  const searchInputRow = searchInputTrailing ? (
    <SearchInputTrailingBarWrapper
      display="flex"
      alignItems="center"
      $pinned={stickySearchInput}
    >
      <Box flex="1 1 0" minWidth={0}>
        {searchInput}
      </Box>
      {searchInputTrailing}
    </SearchInputTrailingBarWrapper>
  ) : connectSearchInputToHeader ? (
    <SearchInputBarWrapper $pinned={stickySearchInput}>
      {searchInput}
    </SearchInputBarWrapper>
  ) : stickySearchInput ? (
    <StickySearchInputWrapper>{searchInput}</StickySearchInputWrapper>
  ) : (
    <Box flex="1 1 0px" mb={2}>
      {searchInput}
    </Box>
  );

  const searchResults = (
    <>
      {additionalActions && <Box mb={2}>{additionalActions}</Box>}

      {splitSourceSearch && renderSplitResults ? (
        <Box position="relative" minHeight={isLoading ? '200px' : 'none'}>
          {isLoading && <StyledLogoSpinner background />}
          {!isLoading &&
            splitSearchResults &&
            renderSplitResults(
              splitSearchResults,
              handleSelectSearchLocationCard,
            )}
        </Box>
      ) : (
        resultsPanel
      )}
    </>
  );

  if (stickySearchInput) {
    return (
      <StickySearchLayout>
        {hasError && (
          <Box mb="2" flexShrink={0}>
            <MessageBlock type="error">
              Unable to search locations, please try again.
            </MessageBlock>
          </Box>
        )}
        {searchInputRow}
        <StickySearchResultsArea>{searchResults}</StickySearchResultsArea>
      </StickySearchLayout>
    );
  }

  return (
    <>
      {hasError && (
        <Box mb="2">
          <MessageBlock type="error">
            Unable to search locations, please try again.
          </MessageBlock>
        </Box>
      )}
      {searchInputRow}
      {searchResults}
    </>
  );
};

export default LocationSearch;
